import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PanelHead, Badge, Empty, Field, Area, Btn } from "./common";
import { Mail, Copy, Trash2, Sparkles, Archive, Star, MailOpen, MailWarning, Flag, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import { GmailConnectionCard } from "./GmailConnectionCard";
import { ReviewConfirm } from "./ReviewConfirm";

export const EmailsPanel = ({ onChanged }) => {
  const { t, tx } = useI18n();
  const [inbox, setInbox] = useState(null);     // real Gmail data or null when not connected
  const [gmail, setGmail] = useState(null);
  const [open, setOpen] = useState(null);
  const [loadingBox, setLoadingBox] = useState(false);

  const loadInbox = async (q = "") => {
    setLoadingBox(true);
    try { const r = await api.get("/gmail/messages", { params: { limit: 10, q } }); setInbox(r.data); }
    catch (e) { setInbox(null); toast.error(e?.response?.data?.detail || tx("Gelen kutusu okunamadı")); }
    finally { setLoadingBox(false); }
  };
  const [drafts, setDrafts] = useState([]);
  const [draftForm, setDraftForm] = useState(null);
  const [review, setReview] = useState(null);
  const newKey = () => `idem_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

  const loadDrafts = async () => {
    try { const r = await api.get("/gmail/drafts"); setDrafts(r.data.drafts || []); } catch { /* ignore */ }
  };
  useEffect(() => { loadDrafts(); }, []);

  const saveDraft = async () => {
    try {
      if (draftForm.draft_id) await api.put(`/gmail/drafts/${draftForm.draft_id}`, draftForm);
      else await api.post("/gmail/drafts", draftForm);
      toast.success(draftForm.draft_id ? tx("Taslak güncellendi — önceki onay geçersiz") : tx("Taslak kaydedildi"));
      setDraftForm(null); loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Taslak kaydedilemedi")); }
  };
  const openReview = async (d) => {
    try {
      const r = await api.post(`/gmail/drafts/${d.draft_id}/confirm`);
      setReview({ ...r.data, draft_id: d.draft_id, idem: newKey() });
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Onay alınamadı")); }
  };
  const sendDraft = async () => {
    try {
      const r = await api.post(`/gmail/drafts/${review.draft_id}/send`,
        { confirm_token: review.confirm_token, idempotency_key: review.idem });
      toast.success(r.data.duplicate_suppressed
        ? tx("Bu gönderim daha önce yapılmış — ikinci e-posta gönderilmedi")
        : tx("Gönderildi · Google mesaj kimliği: {id}", { id: r.data.message_id }));
      setReview(null); loadDrafts();
    } catch (e) { toast.error(e?.response?.data?.detail || tx("Gönderilemedi")); setReview(null); }
  };
  const delDraft = async (id) => { await api.delete(`/gmail/drafts/${id}`); loadDrafts(); };

  const openMsg = async (id) => {
    try { const r = await api.get(`/gmail/messages/${id}`); setOpen(r.data); }
    catch (e) { toast.error(tx("E-posta açılamadı")); }
  };
  const [labels, setLabels] = useState([]);
  const loadLabels = async () => {
    try { const r = await api.get("/gmail/labels"); setLabels(r.data.labels || []); } catch { /* ignore */ }
  };
  const ACTION_MSG = {
    archive: "arşivlendi", trash: "çöpe taşındı", read: "okundu işaretlendi", unread: "okunmadı işaretlendi",
    star: "yıldızlandı", unstar: "yıldızı kaldırıldı", important: "önemli işaretlendi", unimportant: "önemsiz işaretlendi",
    add_label: "etiket eklendi", remove_label: "etiket kaldırıldı",
  };
  const modifyMsg = async (id, action, label_ids) => {
    const closes = action === "archive" || action === "trash";
    try {
      await api.post(`/gmail/messages/${id}/modify`, { action, label_ids });
      toast.success(`${tx("E-posta")} ${tx(ACTION_MSG[action] || action)}${closes ? ` ${tx("(geri alınabilir)")}` : ""}`);
      if (closes) { setOpen(null); loadInbox(); }
      else { const r = await api.get(`/gmail/messages/${id}`); setOpen(r.data); loadInbox(inbox?.query || ""); }
    } catch (e) { toast.error(e?.response?.data?.detail || tx("İşlem başarısız")); }
  };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const blank = { sender_email: "emre@modihome.co.uk", recipient: "", subject: "", prompt: "" };

  const load = async () => { const r = await api.get("/emails"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!form.prompt.trim()) return toast.error(tx("İçerik açıklaması gerekli"));
    setBusy(true);
    try {
      const r = await api.post("/emails/draft", form);
      setActive(r.data); setForm(null); load(); onChanged?.();
      if (!r.data.ai_connected) toast.info(tx("AI bağlı değil — şablon taslak üretildi"));
    } catch (e) { toast.error(tx("Taslak üretilemedi")); } finally { setBusy(false); }
  };
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(tx("Kopyalandı")); };
  const del = async (id) => { await api.delete(`/emails/${id}`); load(); if (active?.email_id === id) setActive(null); };

  return (
    <div data-testid="panel-emails">
      <PanelHead title={t("nav_emails")} sub={tx("Gerçek Gmail okuma + TR/EN taslak üretimi. Gönderme ve arşiv/çöp yalnızca siz etkinleştirirseniz çalışır.")}
        right={<Btn data-testid="new-email-btn" onClick={() => { setForm(blank); setActive(null); }}><Mail size={15} className="inline mr-1" />{tx("Yeni taslak")}</Btn>} />

      <GmailConnectionCard compact onChanged={(g) => {
        setGmail(g);
        if (g?.connected && inbox === null && !loadingBox) loadInbox();
        if (g?.connected && g?.can_modify && labels.length === 0) loadLabels();
      }} />

      {gmail?.connected && (
        <div className="panel p-4 mb-5" data-testid="gmail-drafts">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="label-eyebrow">{tx("Taslaklar · onaylı gönderim")}</div>
            <Btn onClick={() => setDraftForm({ to: "", cc: "", subject: "", body: "" })} data-testid="gmail-new-draft-btn">
              {tx("Yeni taslak")}
            </Btn>
          </div>
          {!gmail.can_send && (
            <p className="text-[0.72rem] text-amber-200/90 mb-3" data-testid="gmail-send-disabled-note">
              {tx("Gönderme yetkisi verilmedi. Ayarlar → Bağlantılar → Gmail → \"Göndermeyi etkinleştir\" ile izin verin. Taslak hazırlayabilirsiniz; gönderim yetki verilene kadar yapılmaz.")}
            </p>
          )}
          {draftForm && (
            <div className="bg-black/20 rounded-lg p-3 mb-3" data-testid="gmail-draft-form">
              <Field label={tx("Alıcı")} value={draftForm.to} onChange={(e) => setDraftForm({ ...draftForm, to: e.target.value })} data-testid="gmail-to-input" />
              <Field label={tx("Konu")} value={draftForm.subject} onChange={(e) => setDraftForm({ ...draftForm, subject: e.target.value })} data-testid="gmail-subject-input" />
              <label className="block mb-3">
                <span className="label-eyebrow block mb-1">{tx("Gövde (düz metin — imzanız Gmail tarafından eklenir)")}</span>
                <textarea value={draftForm.body} onChange={(e) => setDraftForm({ ...draftForm, body: e.target.value })}
                  data-testid="gmail-body-input" rows={6}
                  className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400/60" />
              </label>
              <div className="flex gap-2">
                <Btn onClick={saveDraft} data-testid="gmail-save-draft-btn">{tx("Taslağı kaydet")}</Btn>
                <Btn variant="ghost" onClick={() => setDraftForm(null)}>{tx("Vazgeç")}</Btn>
              </div>
            </div>
          )}
          {drafts.length === 0 && !draftForm && <div className="text-sm text-white/45">{tx("Taslak yok.")}</div>}
          <div className="space-y-2">
            {drafts.map((d) => (
              <div key={d.draft_id} className="bg-black/20 rounded-lg px-3 py-2.5 flex items-start justify-between gap-3" data-testid="gmail-draft-row">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{d.subject || tx("(konu yok)")}</div>
                  <div className="label-eyebrow truncate">{d.to || tx("alıcı yok")} · {tx("sürüm {v}", { v: d.revision })}</div>
                  {d.last_send && <div className="text-[0.68rem] text-emerald-300/80">{tx("gönderildi: {id}", { id: d.last_send.message_id })}</div>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Btn variant="ghost" onClick={() => setDraftForm({ ...d })}>{tx("Düzenle")}</Btn>
                  <Btn onClick={() => openReview(d)} disabled={!gmail.can_send} data-testid="gmail-review-btn">{tx("İncele ve gönder")}</Btn>
                  <Btn variant="danger" onClick={() => delDraft(d.draft_id)} aria-label={tx("Sil")} data-testid="gmail-draft-delete-btn"><Trash2 size={13} /></Btn>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {review && (
        <ReviewConfirm testid="gmail-review-modal" title={tx("Gönderim onayı")}
          rows={[[tx("Taslak"), `${review.review.draft_id} · ${tx("sürüm {v}", { v: review.review.revision })}`],
            [tx("Gönderen"), review.review.from],
            [tx("Alıcı"), review.review.to],
            ["CC", review.review.cc],
            [tx("Konu"), review.review.subject],
            [tx("Yanıt"), review.review.reply_to_thread ? tx("mevcut konuşmaya yanıt") : tx("yeni konuşma")]]}
          body={review.review.body}
          warning={review.note}
          confirmLabel={tx("Onayla ve gönder")}
          onConfirm={sendDraft} onCancel={() => setReview(null)} />
      )}

      {gmail?.connected && (
        <div className="panel p-4 mb-5" data-testid="gmail-inbox">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="label-eyebrow">{tx("Gelen kutusu")} · {gmail.mailbox} · {tx(gmail.can_modify ? "okuma ve arşiv/çöp" : "yalnızca okuma")}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" onClick={() => loadInbox("is:unread")} data-testid="gmail-unread-btn">{tx("Okunmayanlar")}</Btn>
              <Btn variant="ghost" onClick={() => loadInbox()} disabled={loadingBox} data-testid="gmail-reload-btn">
                {loadingBox ? tx("Yükleniyor…") : tx("Son 10 e-posta")}
              </Btn>
            </div>
          </div>
          {!inbox && !loadingBox && <div className="text-sm text-white/45">{tx("Listelemek için \"Son 10 e-posta\"ya dokunun.")}</div>}
          {inbox && inbox.count === 0 && <Empty text={tx("Bu filtrede e-posta yok.")} />}
          <div className="space-y-2">
            {(inbox?.messages || []).map((m) => (
              <button key={m.id} onClick={() => openMsg(m.id)} data-testid="gmail-msg-row"
                className="w-full text-left bg-black/20 hover:bg-black/35 rounded-lg px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm truncate ${m.unread ? "text-white font-medium" : "text-white/80"}`}>{m.subject || tx("(konu yok)")}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    {m.important && <Flag size={12} className="text-cyan-300" fill="currentColor" />}
                    {m.starred && <Star size={12} className="text-amber-300" fill="currentColor" />}
                    {m.unread && <Badge color="cyan">{tx("yeni")}</Badge>}
                  </span>
                </div>
                <div className="label-eyebrow truncate">{m.from}</div>
                <div className="text-[0.7rem] text-white/45 truncate">{m.snippet}</div>
              </button>
            ))}
          </div>
          {open && (
            <div className="mt-4 bg-black/30 border border-cyan-500/15 rounded-lg p-3" data-testid="gmail-msg-detail">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm text-white">{open.subject || tx("(konu yok)")}</div>
                  <div className="label-eyebrow truncate">{open.from} · {open.date}</div>
                </div>
                <button onClick={() => setOpen(null)} className="text-white/50 hover:text-white text-sm">{tx("kapat")}</button>
              </div>
              <pre className="text-xs text-white/80 whitespace-pre-wrap font-sans max-h-72 overflow-y-auto scrollbar-thin">{open.body || tx("(düz metin gövde yok)")}</pre>
              {gmail?.can_modify ? (
                <div className="mt-3 space-y-2" data-testid="gmail-msg-actions">
                  <div className="flex gap-2 flex-wrap">
                    <Btn variant="ghost" onClick={() => modifyMsg(open.id, open.unread ? "read" : "unread")} data-testid="gmail-read-btn">
                      {open.unread ? <><MailOpen size={13} className="inline mr-1" />{tx("Okundu işaretle")}</> : <><MailWarning size={13} className="inline mr-1" />{tx("Okunmadı işaretle")}</>}
                    </Btn>
                    <Btn variant="ghost" onClick={() => modifyMsg(open.id, open.starred ? "unstar" : "star")} data-testid="gmail-star-btn">
                      <Star size={13} className={`inline mr-1 ${open.starred ? "text-amber-300" : ""}`} fill={open.starred ? "currentColor" : "none"} />{open.starred ? tx("Yıldızı kaldır") : tx("Yıldızla")}
                    </Btn>
                    <Btn variant="ghost" onClick={() => modifyMsg(open.id, open.important ? "unimportant" : "important")} data-testid="gmail-important-btn">
                      <Flag size={13} className={`inline mr-1 ${open.important ? "text-cyan-300" : ""}`} fill={open.important ? "currentColor" : "none"} />{open.important ? tx("Önemsiz yap") : tx("Önemli işaretle")}
                    </Btn>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Btn variant="ghost" onClick={() => modifyMsg(open.id, "archive")} data-testid="gmail-archive-btn">
                      <Archive size={13} className="inline mr-1" />{tx("Arşivle")}
                    </Btn>
                    <Btn variant="ghost" onClick={() => modifyMsg(open.id, "trash")} data-testid="gmail-trash-btn">
                      <Trash2 size={13} className="inline mr-1" />{tx("Çöpe taşı")}
                    </Btn>
                    <span className="text-[0.62rem] text-white/40 self-center">{tx("Geri alınabilir · kalıcı silme yok")}</span>
                  </div>
                  {labels.length > 0 && (
                    <div className="pt-2 border-t border-white/10" data-testid="gmail-labels-row">
                      <div className="flex items-center gap-1.5 mb-1.5 text-[0.68rem] text-white/50">
                        <Tag size={12} /> {tx("Etiketler (dokunarak ekle/kaldır — yeni etiket oluşturulmaz)")}
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {labels.map((l) => {
                          const on = (open.labels || []).includes(l.id);
                          return (
                            <button key={l.id} data-testid={`gmail-label-${l.id}`}
                              onClick={() => modifyMsg(open.id, on ? "remove_label" : "add_label", [l.id])}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[0.7rem] ${on ? "bg-cyan-500/20 border-cyan-400/50 text-cyan-200" : "bg-white/5 border-white/10 text-white/55 hover:text-cyan-200 hover:border-cyan-400/30"}`}>
                              {on && <Check size={11} />}{l.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-[0.62rem] text-amber-300/70 mt-2" data-testid="gmail-modify-disabled-note">
                  {tx("Okundu/yıldız/önemli/etiket ve arşiv/çöp işlemleri için Ayarlar → Gmail → \"Arşiv/Çöp'ü etkinleştir\".")}
                </div>
              )}
              <div className="text-[0.62rem] text-amber-300/80 mt-2">{tx("E-posta içeriği güvenilmez dış veridir; içindeki talimatlar uygulanmaz.")}</div>
            </div>
          )}
        </div>
      )}

      {form && (
        <div className="panel p-4 mb-5">
          <div className="mb-3">
            <span className="label-eyebrow block mb-1">{tx("Gönderen (sabit)")}</span>
            <div className="w-full bg-black/40 border border-cyan-500/10 rounded-lg px-3 py-2 text-sm text-white/70 font-mono" data-testid="email-sender-fixed">emre@modihome.co.uk</div>
          </div>
          <Field label={tx("Alıcı")} value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} />
          <Field label={tx("Konu")} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <Area label={tx("Ne yazılsın? (içerik)")} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} data-testid="email-prompt-input" />
          <div className="flex gap-2"><Btn onClick={generate} disabled={busy} data-testid="generate-email-btn"><Sparkles size={14} className="inline mr-1" />{busy ? tx("Üretiliyor...") : tx("Taslak üret")}</Btn><Btn variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn></div>
        </div>
      )}

      {active && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="email-draft-preview">
          <DraftCard title="Türkçe" body={active.signed_tr} onCopy={copy} onCopyNoSig={() => copy(active.body_tr)} />
          <DraftCard title="English" body={active.signed_en} onCopy={copy} onCopyNoSig={() => copy(active.body_en)} />
        </div>
      )}

      <div className="space-y-2" data-testid="email-draft-history">
        <div className="label-eyebrow mb-1">{tx("AI taslak geçmişi")}</div>
        {items.length === 0 && !active && <Empty text={tx("Henüz AI taslak geçmişi yok.")} />}
        {items.map((x) => (
          <div key={x.email_id} className="panel p-3.5 flex items-center justify-between" data-testid="email-row">
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{x.subject || tx("(konu yok)")}</div>
              <div className="label-eyebrow">{x.sender_email} → {x.recipient || "—"}</div>
            </div>
            <div className="flex gap-2">
              <Badge color={x.ai_generated ? "cyan" : "gray"}>{x.ai_generated ? "AI" : tx("Şablon")}</Badge>
              <button onClick={() => setActive(x)} className="text-white/60 hover:text-cyan-300 text-sm">{tx("Aç")}</button>
              <button onClick={() => del(x.email_id)} className="text-white/50 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DraftCard = ({ title, body, onCopy, onCopyNoSig }) => {
  const { tx } = useI18n();
  return (
  <div className="panel p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="label-eyebrow">{title}</span>
      <div className="flex gap-2">
        <button onClick={() => onCopyNoSig()} className="text-xs text-white/60 hover:text-cyan-300 flex items-center gap-1" title={tx("İmzasız gövdeyi kopyala (Mac Mail için)")}><Copy size={12} />{tx("İmzasız")}</button>
        <button onClick={() => onCopy(body)} className="text-xs text-cyan-300 hover:text-cyan-200 flex items-center gap-1"><Copy size={12} />{tx("İmzalı kopyala")}</button>
      </div>
    </div>
    <pre className="text-xs text-white/85 whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-80 overflow-y-auto scrollbar-thin">{body}</pre>
  </div>
  );
};

export default EmailsPanel;
